// Section 12: illness progression and the per-season illness roll.
// Progression: an illness or injury caught in an earlier season rolls its death chance (the
// illnessDeath target, read with this season's care: treated halves it, plague treated 10%),
// then counts down its duration (treated duration once fully treated); wasting lasts until the
// person has been fed in full 3 seasons (2 treated). Illness deaths go through shared/death.ts.
// Roll: base 3% x season x needs x traits x crowding x water x contagion x uncovered under-6
// (all modifiers on illnessRisk); on success the type is drawn by weight from the roll
// illnesses (fever 50, flux 25), or wasting for someone hungry 4+ seasons (ADR-0010 item 28).
// In projection mode the roll and death chances accumulate as expectations (24.8).
import type { Factor, IllnessId, Person, PhaseContext } from '../../core/types';
import { contributingFactors, killPerson } from '../shared/death';
import { applyDelta, applyDeltas } from '../shared/deltas';
import { num } from '../shared/value';
import { careContext } from './healing';

const CARE_NOTES = {
  medicine: 'treated with medicine',
  'no-medicine': 'treated without medicine',
  'passed-over': 'passed over for treatment',
  untreated: 'no healer to treat them',
  visit: 'seen by a healer',
} as const;

/** The care row first, then the three most relevant factors from memory and this season. */
function deathFactors(ctx: PhaseContext, p: Person): Factor[] {
  const care = ctx.report.current.care?.[p.id] ?? 'untreated';
  const row: Factor = {
    source: `care:${care}`,
    op: 'add',
    value: 0,
    delta: 0,
    note: CARE_NOTES[care],
  };
  return [row, ...contributingFactors(ctx, p)].slice(0, 3);
}

function deathRoll(ctx: PhaseContext, p: Person, illnessId: IllnessId): boolean {
  const chance = num(careContext(ctx), { kind: 'illnessDeath', personId: p.id, illnessId });
  const roll = ctx.rng.chance(chance);
  if (ctx.mode === 'projection') {
    ctx.report.current.expectedDeaths += roll;
    return false;
  }
  if (!ctx.rng.happened(roll)) return false;
  const factors = deathFactors(ctx, p);
  if (illnessId === 'injury') killPerson(ctx, p.id, 'injury', { factors });
  else killPerson(ctx, p.id, 'illness', { illnessId, factors });
  return true;
}

/** Progress existing illness and injury; returns false if the person died. */
export function progressIllness(ctx: PhaseContext, p: Person): boolean {
  const turn = ctx.season.turn;
  const sick = p.status.sick;
  if (sick !== null && sick.sinceTurn !== turn) {
    if (deathRoll(ctx, p, sick.illnessId)) return false;
    sick.seasonsSick += 1;
    sick.seasonsRemaining = Math.max(0, sick.seasonsRemaining - 1);
    const def = ctx.registry.illnesses[sick.illnessId];
    const recovered =
      sick.illnessId === 'wasting'
        ? p.streaks.fed >= (sick.treated ? def.treatedSeasons : def.untreatedSeasons)
        : sick.seasonsRemaining <= 0;
    if (recovered) {
      p.status.sick = null;
      p.status.quarantined = false;
      ctx.report.add({ kind: 'recovery', personId: p.id, illnessId: sick.illnessId });
      applyDeltas(ctx, def.onRecover(ctx.live, p));
    }
  }
  const hurt = p.status.injured;
  if (hurt !== null && hurt.sinceTurn !== turn) {
    if (deathRoll(ctx, p, 'injury')) return false;
    hurt.seasonsRemaining = Math.max(0, hurt.seasonsRemaining - 1);
    if (hurt.seasonsRemaining <= 0) {
      p.status.injured = null;
      ctx.report.add({ kind: 'recovery', personId: p.id, illnessId: 'injury' });
    }
  }
  return true;
}

/**
 * Roll for new illness (Section 12); returns the risk rolled, or null for someone already sick.
 * Projection mode adds the chance to `report.expectedIllnesses`.
 */
export function rollIllness(ctx: PhaseContext, p: Person): number | null {
  if (p.status.sick !== null) return null;
  const risk = num(ctx, { kind: 'illnessRisk', personId: p.id });
  const roll = ctx.rng.chance(risk);
  if (ctx.mode === 'projection') {
    const report = ctx.report.current;
    report.expectedIllnesses = (report.expectedIllnesses ?? 0) + roll;
  }
  if (ctx.rng.happened(roll)) sicken(ctx, p);
  return risk;
}

function sicken(ctx: PhaseContext, p: Person): void {
  if (p.streaks.hungry >= ctx.cfg.illness.wastingAfterHungrySeasons) {
    applyDelta(ctx, { kind: 'sicken', personId: p.id, illnessId: 'wasting', source: 'need:food' });
    return;
  }
  const choices = (
    Object.values(ctx.registry.illnesses) as { id: IllnessId; source: string; weight: number }[]
  )
    .filter((d) => d.source === 'roll' && d.weight > 0)
    .map((d) => ({ item: d.id, weight: d.weight }));
  if (choices.length === 0) return;
  applyDelta(ctx, {
    kind: 'sicken',
    personId: p.id,
    illnessId: ctx.rng.weighted(choices),
    source: 'illness:roll',
  });
}
